import { useSnackbar } from "notistack";
import { FC, useEffect, useState } from "react";

import { Button, FormControl, MenuItem, Select, Stack, Switch, TextField } from "@mui/material";

import { api } from "../../../../../utils";
import { IError } from "../../../../../models/ReportPageModels";

interface GetReportUsernamesResponse {
    data: GetReportUsernamesData;
    error: IError;
}

interface GetReportUsernamesData {
    usernames: string[];
}

export interface BillingCompanyModel {
    id: number;
    branchName: string;
}

export interface AdminGetBillingCompaniesResponse {
    data: BillingCompaniesData;
    error: IError;
}

interface BillingCompaniesData {
    companies: BillingCompanyModel[];
}

interface CreateReportUserRequest {
    username: string;
    password: string;
    company: number;
    imageType: string;
    imageDetails: string;
    booth: string;
    useReview: boolean;
    reviewImagePrefix: string;
}

export interface CreateBillingUserResponse {
    data: CreateBillingUserData;
    error: IError;
}
interface CreateBillingUserData {
    status: string;
}

export const allImageTypes: string[] = ["s3", "gcp", "cargiant", "smh"];

interface CreateReportUserProps {
    workflowNextStep?: () => void;
    createdBranchName?: string | null;
}

const CreateReportUser: FC<CreateReportUserProps> = ({ workflowNextStep = null, createdBranchName = null }) => {
    const { enqueueSnackbar } = useSnackbar();

    const [username, setUsername] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [selectedCompany, setSelectedCompany] = useState<number>(0);
    const [selectedImageType, setSelectedImageType] = useState<string>("s3");
    const [imageDetails, setImageDetails] = useState<string>("");
    const [boothFilter, setBoothFilter] = useState<string>("");
    const [useReview, setUseReview] = useState<boolean>(false);
    const [reviewImagePrefix, setReviewImagePrefix] = useState<string>("");

    const [companies, setCompanies] = useState<BillingCompanyModel[]>([]);

    const [usernames, setUsernames] = useState<string[]>([]);

    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        GetReportUsernames();
        GetCompanies();
    }, []);

    const GetReportUsernames = async () => {
        try {
            const response: GetReportUsernamesResponse = await api.get("api/Admin/GetReportUsernames", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setUsernames(response.data.usernames);
                setIsLoading(false);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    };

    const GetCompanies = async () => {
        try {
            const response: AdminGetBillingCompaniesResponse = await api.get("api/Admin/GetBillingCompanies", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setCompanies(response.data.companies);

                // branch created in workflow previous step
                if (createdBranchName) {
                    const company = response.data.companies.find(c => c.branchName === createdBranchName);
                    if (company) {
                        setSelectedCompany(company.id);
                    }
                }
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    };

    const CreateClient = async () => {
        if (username === "" || password === "" || selectedCompany === 0) {
            enqueueSnackbar("Please recheck fields", { variant: "error" });
            return;
        }

        var body: CreateReportUserRequest = {
            username: username,
            password: password,
            company: selectedCompany,
            imageType: selectedImageType,
            imageDetails: imageDetails,
            booth: boothFilter,
            useReview: useReview,
            reviewImagePrefix: reviewImagePrefix
        };

        try {
            const response: CreateBillingUserResponse = await api.post("api/Admin/CreateReportUser", {
                json: body,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                enqueueSnackbar("Created!", { variant: "success" });

                // for workflow function to step passed
                if (workflowNextStep)
                    workflowNextStep();

                // clear fields
                setUsername("");
                setPassword("");
                setSelectedCompany(0);
                setSelectedImageType("s3");
                setImageDetails("");
                setBoothFilter("");
                setUseReview(false);
                setReviewImagePrefix("");
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    };

    const IsUsernameValid = !usernames.includes(username);
    const IsFormValid = IsUsernameValid && username !== "" && password !== "" && selectedCompany !== 0;

    return (
        <Stack>
            {/* Username */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Username: </p>
                <TextField
                    value={username}
                    onChange={(newValue: any) => { setUsername(newValue.target.value); }}
                    sx={{ minWidth: 245 }}
                    size="small"
                    error={!IsUsernameValid}
                    helperText={!IsUsernameValid ? "Username is already in use" : ""}
                    disabled={isLoading} />
            </Stack>

            {/* Password */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Password: </p>
                <TextField
                    value={password}
                    onChange={(newValue: any) => { setPassword(newValue.target.value); }}
                    sx={{ minWidth: 245 }}
                    size="small" />
            </Stack>

            {/* Company */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Company: </p>
                <FormControl sx={{ minWidth: "245px" }}>
                    <Select
                        value={selectedCompany}
                        onChange={(newValue: any) => { setSelectedCompany(newValue.target.value); }}
                        size="small">
                        <MenuItem
                            value={0}>
                            {"Select company"}
                        </MenuItem>
                        {companies.map((company, index) => <MenuItem
                            key={index}
                            value={company.id}>
                            {company.branchName}
                        </MenuItem>
                        )}
                    </Select>
                </FormControl>
            </Stack>

            {/* ImageType */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Image type: </p>
                <FormControl sx={{ minWidth: "245px" }}>
                    <Select
                        value={selectedImageType}
                        onChange={(newValue: any) => { setSelectedImageType(newValue.target.value); }}
                        size="small">
                        {allImageTypes.map((type, index) => <MenuItem
                            key={index}
                            value={type}>
                            {type}
                        </MenuItem>
                        )}
                    </Select>
                </FormControl>
            </Stack>

            {/* ImageDetails */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Image details: </p>
                <TextField
                    value={imageDetails}
                    onChange={(newValue: any) => { setImageDetails(newValue.target.value); }}
                    sx={{ minWidth: 245 }}
                    size="small" />
            </Stack>

            {/* BoothIdFilter */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>BoothId filter: </p>
                <TextField
                    value={boothFilter}
                    onChange={(newValue: any) => { setBoothFilter(newValue.target.value); }}
                    sx={{ minWidth: 245 }}
                    size="small" />
                <p style={{ color: "gray" }}> Exact BoothId or part of Boothid  in "%". Example: %_B2C%</p>
            </Stack>

            {/* Use Review */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Use Review: </p>
                <Switch
                    checked={useReview}
                    onChange={(newValue) => { setUseReview(newValue.target.checked) }}
                    size="small" />
            </Stack>
            {/* review Image url Prefix */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Review Image URL Prefix: </p>
                <TextField
                    value={reviewImagePrefix}
                    onChange={(newValue: any) => { setReviewImagePrefix(newValue.target.value); }}
                    sx={{ minWidth: 245 }}
                    size="small" />
            </Stack>


            <Button
                variant="outlined"
                onClick={() => CreateClient()}
                sx={{ width: '82px' }}
                disabled={!IsFormValid}>
                Create
            </Button>
        </Stack>
    );
}

export default CreateReportUser;